import { Injectable, OnDestroy } from '@angular/core';
// import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
// import { Action, Store } from '@ngrx/store';

import { ToastrService } from 'ngx-toastr';
import { Subject, of } from 'rxjs';
import { switchMap, mergeMap, delay, catchError, withLatestFrom } from 'rxjs/operators';
import { LayersService, WMRPService } from 'src/app/services';
import {
    SetDefaultConditions, SetMaterialsDictionary, SetWMRPModule, SetTemplateNames, SetTemplateParameters, WMRPActionType
    , GetTemplateNames, GetTemplateParameters, SaveTemplateParametersSuccess, SaveAnalysisDetailsSuccess
    , SetRecentAnalysisDetails, SaveAnalysisDetails, ShowExpandParametersBtn, GetAnalysisDetails
    , AddRecentAnalysisDetails, SetReplacementSchedules, SetWmrtResults, GetReplacementSchedules, GetExclusionLayerMains, GetExclusionLayerMainsSuccess, GetExclusionLayerMainsError, UpdateScheduleYear,
    SaveAnalysisTitle,
    DeleteAnalysisRun,
    SaveScheduleTitle,
    DeleteSchedule
} from './wmrp.action';
import { WmrpAnalysisParamSet, WmrpAnalysisParam, WmrpAnalysisRun, WMRPState } from './wmrp.state';

import { ApplicationActionType, CheckLayerDisplay, ToastUserNotification, SetSelectedFeatureSections } from 'src/app/state-management/application/application.action';
import { BaseMapState, DnvFeatureLayer, DnvLegendState, DnvMapActionType, DnvMapState, DnvSearchState, LayerState, MapViewState, PropertiesState, DnvFeatureService, UpdateLayer } from 'dnv-lib';
import { HttpErrorResponse } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { RouterReducerState } from '@ngrx/router-store';
import { AuthState } from '../auth';
import { ApplicationState } from '../application/application.state';
// src\app\state-management\application\application.action.ts

export interface State {
    mapState: DnvMapState;
    layerState: LayerState;
    legendState: DnvLegendState;
    baseMapState: BaseMapState;
    dnvSearchState: DnvSearchState;
    applicationReducer: ApplicationState;
    authReducer: AuthState;
    routerReducer: RouterReducerState;
    mapViewState: MapViewState;
    propertiesState: PropertiesState;
    wmrpState: WMRPState;
}

@Injectable()
export class WMRPEffects implements OnDestroy {
    private ngUnsubscribe: Subject<void> = new Subject();

    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

    GetWMRPModule$ = createEffect((): any => this.actions$.pipe(
        ofType(WMRPActionType.GetWMRPModule),
        switchMap((action: any) => this.wmrpService.getWMRPModule()
            .pipe(
                mergeMap((data: any, payload: any) => {
                    return [
                        new SetWMRPModule(data)
                    ];
                }
                )
            )
        )
    ));

    GetMaterialsDictionary$ = createEffect((): any => this.actions$.pipe(
        ofType(WMRPActionType.GetMaterialsDictionary),
        switchMap((action: any) => this.wmrpService.getMaterials()
            .pipe(
                mergeMap((data: any, payload: any) => {
                    return [
                        new SetMaterialsDictionary(data)
                    ];
                }
                )
            )
        )
    ));

    GetTemplateNames$ = createEffect(() => this.actions$.pipe(
        ofType(WMRPActionType.GetTemplateNames),
        switchMap((action: GetTemplateNames) => this.wmrpService.getWMRPTemplates()
            .pipe(
                mergeMap((data: WmrpAnalysisParamSet[], payload: any) => {
                    const defaultTemplateId = data.filter(d => d.isDefault === true)[0].id;

                    return [
                        new SetTemplateNames(data),
                        new GetTemplateParameters(defaultTemplateId)
                    ];
                }
                )
            )
        )
    ));

    GetTemplateParameters$ = createEffect(() => this.actions$.pipe(
        ofType(WMRPActionType.GetTemplateParameters),
        switchMap((action: GetTemplateParameters) => this.wmrpService.getWMRPParameters(action.payload)
            .pipe(
                mergeMap((data: WmrpAnalysisParam[], payload: any) => {
                    // [{"id":1,"wmrpAnalysisParamSet_Id":1,"wmrpModule_Id":1,"enabled":true,"conditions":"[{\"break-value\": \"AC,CONCRETE\", \"analysis-value\": \"1\"}]"}]

                    const parsedData = data.map(d => {
                        return {
                            ...d,
                            conditions: JSON.parse(d.conditions as string),
                            moduleId: d.wmrpModule_Id
                        };
                    });
                    return [
                        new SetTemplateParameters(parsedData)
                    ];
                }
                )
            )
        )
    ));

    SaveTemplateParameters$ = createEffect(() => this.actions$.pipe(
        ofType(WMRPActionType.SaveTemplateParameters),
        switchMap((action: any) => this.wmrpService.postWMRPParameters(action.payload)
            .pipe(
                catchError(() => of([])),
                mergeMap((data: any, payload: any) => {
                    // const analysisDetails: WmrpAnalysisRun = {
                    //     wmrpAnalysisParamSet_Id: data.wmrpParamSetId,
                    //     fmeJobId: 'test',
                    //     status: 'success', // todo: update
                    //     fmeReference: 'test'
                    // }
                    return [
                        new SaveTemplateParametersSuccess(data),
                        // new SaveAnalysisDetails(analysisDetails),
                        // new RunAnalysis(data.wmrpAnalysisParams)
                        new GetTemplateNames(),
                        new ToastUserNotification({
                            type: 'success',
                            title: 'Template saved',
                            message: 'Template was successfully saved',
                            options: {
                                disableTimeOut: false,
                                closeButton: true
                            }
                        }),
                    ];
                }
                )
            )
        )
    ));

    SaveAnalysisDetails$ = createEffect(() => this.actions$.pipe(
        ofType(WMRPActionType.SaveAnalysisDetails),
        switchMap((action: any) => this.wmrpService.postAnalysisRunDetails(action.payload)
            .pipe(
                mergeMap((data: any, payload: any) => {
                    return [
                        new SaveAnalysisDetailsSuccess(data),
                        new GetAnalysisDetails()
                    ];
                }
                )
            )
        )
    ));

    GetAnalysisDetails$ = createEffect(() => this.actions$.pipe(
        ofType(WMRPActionType.GetAnalysisDetails),
        switchMap((action: any) => this.wmrpService.getAnalysisRunDetails()
            .pipe(
                mergeMap((data: any, payload: any) => {
                    return [
                        new SetRecentAnalysisDetails(data)
                    ];
                }),
                catchError((error: HttpErrorResponse) => {
                    return of(new ToastUserNotification({
                        type: 'fail',
                        title: 'The operation failed',
                        message: 'Cannot get analysis run status',
                        options: {
                            disableTimeOut: false,
                            closeButton: true,
                            enableHtml: true,
                            timeOut: 5000
                        }
                    }));
                })
            )
        )
    ));

    RunAnalysisCustom$ = createEffect(() => this.actions$.pipe(
        ofType(WMRPActionType.RunAnalysisCustom),
        switchMap((action: any) => this.wmrpService.postRunAnalysisCustom(action.payload)
            .pipe(
                mergeMap((data: any, payload: any) => {
                    return [
                        new AddRecentAnalysisDetails(data),
                        new GetAnalysisDetails()
                    ];
                }
                )
            )
        ),
        catchError((error: HttpErrorResponse) => {
            let msg = 'The operation failed';
            if (error.status === 403) {
                msg = 'You do not have permission to perform this operation'
            }
            return of(new ToastUserNotification({
                type: 'fail',
                title: 'Error',
                message: msg,
                options: {
                    disableTimeOut: false,
                    closeButton: true,
                    enableHtml: true,
                    timeOut: 5000
                }
            }));
        })
    ));

    RunAnalysisTemplate$ = createEffect(() => this.actions$.pipe(
        ofType(WMRPActionType.RunAnalysisTemplate),
        switchMap((action: any) => this.wmrpService.postRunAnalysisTemplate(action.payload)
            .pipe(
                mergeMap((data: any, payload: any) => {
                    return [
                        new AddRecentAnalysisDetails(data),
                        new GetAnalysisDetails()
                    ];
                }
                )
            )
        ),
        catchError((error: HttpErrorResponse) => {
            let msg = 'The operation failed';
            if (error.status === 403) {
                msg = 'You do not have permission to perform this operation'
            }
            return of(new ToastUserNotification({
                type: 'fail',
                title: 'Error',
                message: msg,
                options: {
                    disableTimeOut: false,
                    closeButton: true,
                    enableHtml: true,
                    timeOut: 5000
                }
            }));
        })
    ));

    StopAnalysisResultsPolling$ = createEffect(() => this.actions$.pipe(
        ofType(WMRPActionType.StopAnalysisResultsPolling),
        switchMap(async () => this.wmrpService.stopPoll())
    ),
        { dispatch: false });

    StopSchedulePolling$ = createEffect(() => this.actions$.pipe(
        ofType(WMRPActionType.StopSchedulePolling),
        switchMap(async () => this.wmrpService.stopSchedulePoll())
    ),
        { dispatch: false });


    generateReplacementSchedule$ = createEffect(() => this.actions$.pipe(
        ofType(WMRPActionType.GenerateReplacementSchedule),
        switchMap((action: any) => this.wmrpService.postGenerateReplacementSchedule(action.payload)
            .pipe(
                mergeMap((data: any, payload: any) => {
                    return [
                        new GetReplacementSchedules(data.fmeReference_Job)
                    ]
                })

            )),
        catchError((error: HttpErrorResponse) => {
            let msg = 'The operation failed';
            if (error.status === 403) {
                msg = 'You do not have permission to perform this operation'
            }
            return of(new ToastUserNotification({
                type: 'fail',
                title: 'Error',
                message: msg,
                options: {
                    disableTimeOut: false,
                    closeButton: true,
                    enableHtml: true,
                    timeOut: 5000
                }
            }));
        })
    ));

    getReplacementSchedules$ = createEffect(() => this.actions$.pipe(
        ofType(WMRPActionType.GetReplacementSchedules),
        switchMap((action: any) => this.wmrpService.getReplacementSchedules(action.payload)
            .pipe(
                mergeMap((data: any, payload: any) => {
                    return [
                        new SetReplacementSchedules(data)
                    ]
                })

            )),
        catchError((error: HttpErrorResponse) => {
            return of(new ToastUserNotification({
                type: 'fail',
                title: 'The operation failed',
                message: 'Cannot get replacement schedule status',
                options: {
                    disableTimeOut: false,
                    closeButton: true,
                    enableHtml: true,
                    timeOut: 5000
                }
            }));
        })
    ));

    getWmrtResults$ = createEffect(() => this.actions$.pipe(
        ofType(WMRPActionType.GetWmrtResults),
        switchMap((action: any) => this.wmrpService.getWmrtResults(action.payload)
            .pipe(
                mergeMap((data: any, payload: any) => {
                    return [
                        new SetWmrtResults(data)
                    ]
                })

            ))
    ));

    setWmrtResults$ = createEffect(() => this.actions$.pipe(
        ofType(WMRPActionType.SetWmrtResults, WMRPActionType.SetWmrtBoundary),
        switchMap((action: any) => {
            return [
                new CheckLayerDisplay()
            ]
        })
    ));

    AddEditFeature$ = createEffect(() => this.actions$.pipe(
        ofType(DnvMapActionType.AddEditFeature),
        switchMap((action: any) => {
            return [
                new GetExclusionLayerMains()
            ]
        })
    ));

    GetExclusionLayerMains$ = createEffect((): any => this.actions$.pipe(
        ofType(WMRPActionType.GetExclusionLayerMains),
        withLatestFrom(this.store$),
        switchMap(([action, store]: [GetExclusionLayerMains, State]): any => {

            //const dnvFeatureLayer: DnvFeatureLayer = (action as GetExclusionLayerMains).payload.dnvFeatureLayer;
            const exclusionLayer: DnvFeatureLayer = store.layerState.layers.find(layer => layer.uid === 'WMRP_EXCLUSION_LAYERS')?.featureLayers[0];
            if (exclusionLayer) {

                let geometry: any[] = []; // geometry not needed for exclusion layer mains

                return this.layersService.getGridSelectLayer(exclusionLayer, geometry).pipe(
                    mergeMap((data: any) => {
                        return [
                            new GetExclusionLayerMainsSuccess({
                                dnvFeatureLayer: exclusionLayer,
                                success: data
                            })
                        ];
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(new GetExclusionLayerMainsError({
                            dnvFeatureLayer: exclusionLayer,
                            error
                        }));
                    })
                );
            }
            else {
                return [];
            }
        })
    ));

    UpdateScheduleYear$ = createEffect(() => this.actions$.pipe(
        ofType(WMRPActionType.UpdateScheduleYear),
        withLatestFrom(this.store$),
        switchMap(([action, store]: [UpdateScheduleYear, State]) => {
            return this.dnvFeatureService.featureLayerUpdateFeature(action.payload.feature, action.payload.url, store.applicationReducer.arcGisToken.token)
                .pipe(mergeMap((data: any[]) => {
                    if (data && data.length > 1 && data[0] == null && data[1]) {
                        const targetScheduleLayer = store.layerState.layers.find(layer => layer.uid === 'WMRP_SCHEDULE_LAYERS');
                        if (targetScheduleLayer != null) {
                            let scheduleLayer = Object.assign({}, targetScheduleLayer);
                            scheduleLayer.featureLayers = scheduleLayer.featureLayers.map(
                                fl => Object.assign({}, fl,
                                    {
                                        symbology: Object.assign({}, fl.symbology, { timestamp: new Date().toISOString() })
                                    }
                                )
                            );

                            const kvSectionsCopy = store.applicationReducer.kvSections.map((kvSection => Object.assign({}, kvSection, {
                                kvPair: kvSection.kvPair.map((pair) => {
                                    if (pair[0] == 'Replacement year') return [pair[0], action.payload.feature.properties.Replacement_year];
                                    if (pair[0] == 'Manual override') return [pair[0], action.payload.feature.properties.Manual_override];
                                    if (pair[0] == 'Deferral reason') return [pair[0], action.payload.feature.properties.Deferral_reason];
                                    return Object.assign({}, pair);
                                })
                            })))

                            return [
                                new UpdateLayer(scheduleLayer),
                                new CheckLayerDisplay(),
                                new SetSelectedFeatureSections(kvSectionsCopy)
                            ];
                        }
                    }
                    return [
                        new ToastUserNotification({
                            type: 'fail',
                            title: 'The operation failed',
                            message: 'Failed to override schedule',
                            options: {
                                disableTimeOut: false,
                                closeButton: true,
                                enableHtml: true,
                                timeOut: 5000
                            }
                        })
                    ];
                }));
        })
    ));

    SaveAnalysisTitle$ = createEffect(() => this.actions$.pipe(
        ofType(WMRPActionType.SaveAnalysisTitle),
        switchMap((action: SaveAnalysisTitle) => this.wmrpService.saveAnalysisTitle({ fmeJobId: action.payload.id, title: action.payload.title })
            .pipe(
                mergeMap((payload: any) => {

                    return [
                        new GetAnalysisDetails(),
                        new ToastUserNotification({
                            type: 'success',
                            title: 'Title saved',
                            message: 'Title was successfully saved for Analysis Run Id ' + action.payload.id,
                            options: {
                                disableTimeOut: false,
                                closeButton: true
                            }
                        }),
                    ];
                }),
                catchError((error: HttpErrorResponse) => {
                    return of(new ToastUserNotification({
                        type: 'fail',
                        title: 'The operation failed',
                        message: 'Cannot set title for analysis run ' + action.payload.id,
                        options: {
                            disableTimeOut: false,
                            closeButton: true,
                            enableHtml: true,
                            timeOut: 5000
                        }
                    }));
                })
            )
        )
    ));

    SaveScheduleTitle$ = createEffect(() => this.actions$.pipe(
        ofType(WMRPActionType.SaveScheduleTitle),
        switchMap((action: SaveScheduleTitle) => this.wmrpService.saveScheduleTitle({ fmeJobId: action.payload.id, title: action.payload.title })
            .pipe(
                mergeMap((payload: any) => {

                    return [
                        new GetReplacementSchedules(action.payload.fmeJobRef),
                        new ToastUserNotification({
                            type: 'success',
                            title: 'Title saved',
                            message: 'Title was successfully saved for Schedule Id ' + action.payload.id,
                            options: {
                                disableTimeOut: false,
                                closeButton: true
                            }
                        }),
                    ];
                }),
                catchError((error: HttpErrorResponse) => {
                    return of(new ToastUserNotification({
                        type: 'fail',
                        title: 'The operation failed',
                        message: 'Cannot set title for schedule ' + action.payload.id,
                        options: {
                            disableTimeOut: false,
                            closeButton: true,
                            enableHtml: true,
                            timeOut: 5000
                        }
                    }));
                })
            )
        )
    ));

    DeleteAnalysisRun$ = createEffect(() => this.actions$.pipe(
        ofType(WMRPActionType.DeleteAnalysisRun),
        switchMap((action: DeleteAnalysisRun) => this.wmrpService.DeleteAnalysisRun(action.payload)
            .pipe(
                mergeMap((payload: any) => {

                    return [
                        new GetAnalysisDetails(),
                        new ToastUserNotification({
                            type: 'success',
                            title: 'Analysis Run deleted',
                            message: 'Analysis Run ' + action.payload + ' was successfully deleted.',
                            options: {
                                disableTimeOut: false,
                                closeButton: true
                            }
                        }),
                    ];
                }),
                catchError((error: HttpErrorResponse) => {
                    return of(new ToastUserNotification({
                        type: 'fail',
                        title: 'The operation failed',
                        message: 'Analysis Run ' + action.payload + ' was not deleted.',
                        options: {
                            disableTimeOut: false,
                            closeButton: true,
                            enableHtml: true,
                            timeOut: 5000
                        }
                    }));
                })
            )
        )
    ));

    DeleteSchedule$ = createEffect(() => this.actions$.pipe(
        ofType(WMRPActionType.DeleteSchedule),
        switchMap((action: DeleteSchedule) => this.wmrpService.DeleteSchedule(action.payload.jobId)
            .pipe(
                mergeMap((payload: any) => {

                    return [
                        new GetReplacementSchedules(action.payload.fmeJobRef),
                        new ToastUserNotification({
                            type: 'success',
                            title: 'Schedule deleted',
                            message: 'Schedule ' + action.payload.jobId + ' was successfully deleted.',
                            options: {
                                disableTimeOut: false,
                                closeButton: true
                            }
                        }),
                    ];
                }),
                catchError((error: HttpErrorResponse) => {
                    return of(new ToastUserNotification({
                        type: 'fail',
                        title: 'The operation failed',
                        message: 'Schedule ' + action.payload.jobId + ' was not deleted.',
                        options: {
                            disableTimeOut: false,
                            closeButton: true,
                            enableHtml: true,
                            timeOut: 5000
                        }
                    }));
                })
            )
        )
    ));

    constructor(
        private actions$: Actions,
        private toastr: ToastrService,
        private wmrpService: WMRPService,
        private store$: Store<State>,
        private layersService: LayersService,
        private dnvFeatureService: DnvFeatureService,
    ) { }

}
